import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Divider,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import ComingSoon from "../widgets/ComingSoon";

/**
 * WhatsApp Business Template Builder (improved)
 * -------------------------------------------------
 * - Character limits enforced & shown live
 * - Better sectioning / spacing with Grid + Divider
 * - Quick validation for button count rules (3 QR OR 2 CTA)
 * - Snackbar notifications instead of alert()
 *
 *  ENV required:
 *    REACT_APP_WABA_ID
 *    REACT_APP_WABA_TOKEN
 */

const CATEGORIES = ["MARKETING", "UTILITY", "AUTHENTICATION", "SERVICE"];
const LANGUAGES = ["en_US", "hi", "es", "pt_BR"];
const LIMITS = {
  name: 512,
  headerText: 60,
  bodyText: 1024,
  footerText: 60,
  buttonText: 20,
};

export default function TemplateBuilder() {
  // ────────────────────────────── state ──────────────────────────────
  const [name, setName] = useState("");
  const [category, setCategory] = useState("UTILITY");
  const [language, setLanguage] = useState("en_US");

  const [headerType, setHeaderType] = useState("NONE");
  const [headerText, setHeaderText] = useState("");
  const [headerMedia, setHeaderMedia] = useState(null);

  const [bodyText, setBodyText] = useState("");
  const [footerText, setFooterText] = useState("");

  const [buttons, setButtons] = useState([]); // {type,text,phone,url}
  const [btnDialogOpen, setBtnDialogOpen] = useState(false);
  const [btnDraft, setBtnDraft] = useState({
    type: "QUICK_REPLY",
    text: "",
    phone: "",
    url: "",
  });

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  // ───────────────────────────── helpers ────────────────────────────
  const qrCount = buttons.filter((b) => b.type === "QUICK_REPLY").length;
  const ctaCount = buttons.filter((b) => b.type === "CALL_TO_ACTION").length;

  const buttonLimitReached =
    (btnDraft.type === "QUICK_REPLY" && qrCount >= 3) ||
    (btnDraft.type === "CALL_TO_ACTION" && ctaCount >= 2) ||
    buttons.length >= 3; // absolute max 3 buttons total

  const addButton = () => {
    if (buttonLimitReached) return;
    setButtons((b) => [...b, btnDraft]);
    setBtnDraft({ type: "QUICK_REPLY", text: "", phone: "", url: "" });
    setBtnDialogOpen(false);
  };

  const deleteButton = (i) =>
    setButtons((b) => b.filter((_, idx) => idx !== i));

  const buildPayload = () => {
    const components = [];
    if (headerType !== "NONE") {
      const header = { type: "HEADER", format: headerType };
      if (headerType === "TEXT") header.text = headerText;
      else header.example = { header_handle: "{{media_url}}" };
      components.push(header);
    }
    components.push({ type: "BODY", text: bodyText });
    if (footerText.trim())
      components.push({ type: "FOOTER", text: footerText });
    if (buttons.length) {
      components.push({
        type: "BUTTONS",
        buttons: buttons.map((b) =>
          b.type === "QUICK_REPLY"
            ? { type: "QUICK_REPLY", text: b.text }
            : {
                type: "CALL_TO_ACTION",
                text: b.text,
                phone_number: b.phone || undefined,
                url: b.url || undefined,
              }
        ),
      });
    }
    return {
      name: name.toLowerCase().replace(/\s+/g, "_"),
      category,
      language,
      components,
    };
  };

  const handleSubmit = async () => {
    try {
      const data = buildPayload();
      await axios.post(
        `https://graph.facebook.com/v18.0/${process.env.REACT_APP_WABA_ID}/message_templates`,
        data,
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_WABA_TOKEN}`,
            "Content-Type": "application/json",
          },
        }
      );
      setSnack({
        open: true,
        msg: "Template submitted for approval!",
        sev: "success",
      });
    } catch (err) {
      console.error(err.response?.data || err);
      setSnack({
        open: true,
        msg: "Submission failed. Check console.",
        sev: "error",
      });
    }
  };

  return <ComingSoon />;

  // ─────────────────────────────── UI ────────────────────────────────
  return (
    <Paper
      elevation={4}
      sx={{ p: { xs: 2, md: 4 }, maxWidth: 1000, mx: "auto" }}
    >
      <Typography variant="h4" gutterBottom>
        WhatsApp Template Builder
      </Typography>

      <Grid container spacing={3}>
        {/* General */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Template Name (snake_case)"
            fullWidth
            inputProps={{ maxLength: LIMITS.name }}
            helperText={`${name.length}/${LIMITS.name}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Language</InputLabel>
            <Select
              value={language}
              label="Language"
              onChange={(e) => setLanguage(e.target.value)}
            >
              {LANGUAGES.map((l) => (
                <MenuItem key={l} value={l}>
                  {l}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }}>Header</Divider>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Header Type</InputLabel>
            <Select
              value={headerType}
              label="Header Type"
              onChange={(e) => setHeaderType(e.target.value)}
            >
              {["NONE", "TEXT", "IMAGE", "VIDEO", "DOCUMENT"].map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {headerType === "TEXT" && (
          <Grid item xs={12} md={8}>
            <TextField
              label="Header Text"
              fullWidth
              inputProps={{ maxLength: LIMITS.headerText }}
              helperText={`${headerText.length}/${LIMITS.headerText}`}
              value={headerText}
              onChange={(e) => setHeaderText(e.target.value)}
            />
          </Grid>
        )}
        {["IMAGE", "VIDEO", "DOCUMENT"].includes(headerType) && (
          <Grid item xs={12} md={8}>
            <Button component="label" variant="outlined" fullWidth>
              Upload {headerType.toLowerCase()}
              <input
                type="file"
                hidden
                onChange={(e) => setHeaderMedia(e.target.files[0])}
              />
            </Button>
          </Grid>
        )}

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }}>Body & Footer</Divider>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Body (use {{1}} for variables)"
            multiline
            fullWidth
            minRows={4}
            inputProps={{ maxLength: LIMITS.bodyText }}
            helperText={`${bodyText.length}/${LIMITS.bodyText}`}
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Footer (optional)"
            fullWidth
            inputProps={{ maxLength: LIMITS.footerText }}
            helperText={`${footerText.length}/${LIMITS.footerText}`}
            value={footerText}
            onChange={(e) => setFooterText(e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }}>Buttons</Divider>
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1">Buttons</Typography>
            <IconButton
              size="small"
              onClick={() => setBtnDialogOpen(true)}
              disabled={buttonLimitReached}
            >
              <AddIcon />
            </IconButton>
            <Typography variant="caption" color="text.secondary">
              {qrCount}/3 QR, {ctaCount}/2 CTA (max 3 total)
            </Typography>
          </Stack>
        </Grid>
        {buttons.map((b, idx) => (
          <Grid item xs={12} md={6} key={idx}>
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body2">
                {b.type} – {b.text}
              </Typography>
              <IconButton size="small" onClick={() => deleteButton(idx)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Paper>
          </Grid>
        ))}

        {/* JSON preview */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }}>Preview JSON</Divider>
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              bgcolor: "#f9f9f9",
              p: 2,
              borderRadius: 1,
              fontSize: 14,
              overflowX: "auto",
            }}
          >
            <pre style={{ margin: 0 }}>
              {JSON.stringify(buildPayload(), null, 2)}
            </pre>
          </Box>
        </Grid>

        <Grid item xs={12} sx={{ textAlign: "right" }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!name || !bodyText}
          >
            Submit to WABA
          </Button>
        </Grid>
      </Grid>

      {/* Button dialog */}
      <Dialog open={btnDialogOpen} onClose={() => setBtnDialogOpen(false)}>
        <DialogTitle>Add Button</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={btnDraft.type}
              label="Type"
              onChange={(e) =>
                setBtnDraft((d) => ({ ...d, type: e.target.value }))
              }
            >
              <MenuItem value="QUICK_REPLY">Quick Reply</MenuItem>
              <MenuItem value="CALL_TO_ACTION">Call To Action</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Button Text"
            inputProps={{ maxLength: LIMITS.buttonText }}
            helperText={`${btnDraft.text.length}/${LIMITS.buttonText}`}
            value={btnDraft.text}
            onChange={(e) =>
              setBtnDraft((d) => ({ ...d, text: e.target.value }))
            }
            sx={{ mb: 2 }}
          />
          {btnDraft.type === "CALL_TO_ACTION" && (
            <>
              <TextField
                fullWidth
                label="Phone Number (optional)"
                value={btnDraft.phone}
                onChange={(e) =>
                  setBtnDraft((d) => ({ ...d, phone: e.target.value }))
                }
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="URL (optional)"
                value={btnDraft.url}
                onChange={(e) =>
                  setBtnDraft((d) => ({ ...d, url: e.target.value }))
                }
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBtnDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={addButton}
            disabled={!btnDraft.text.trim() || buttonLimitReached}
            variant="contained"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
